import { AbstractControl, FormArray, FormGroup, ValidatorFn } from '@angular/forms';
import { MinimumPayment } from 'my-account/constants';
import { Util } from 'services/util';

export class FormUtil {
    public static blurActiveControl(onlyFormElements: boolean = true): void {
        const control: HTMLElement = <HTMLElement> document.activeElement;
        const tag: string = control.tagName;

        if ((!onlyFormElements) || (tag === 'INPUT' || tag === 'SELECT')) {
            control.blur();
        }
    }

    public static focusFirstError(): void {
        setTimeout(() => {
            const element: HTMLElement = document.querySelector('input.ng-invalid, select.ng-invalid, [xmRadioControl].ng-invalid input, xm-dropdown.ng-invalid .select-box');

            if (element) {
                if (!Util.isElementVisible(element)) {
                    /*
                     * 1. Get the browser height. On mobile, we prioritize clientHeight because the view can change sizes based on browser controls
                     * 2. Find the quarter point (half of half of the total height)
                     * 3. Calculate the scroll distance between the element and the quarter point
                     * 4. Scroll to the field based on its current position
                    */
                    const midPoint: number = (document.documentElement.clientHeight || window.innerHeight)/2;
                    const quarterPoint: number = midPoint/2;
                    const scrollDistance: number = element.getBoundingClientRect().top - quarterPoint;
                    window.scroll(0, window.scrollY + scrollDistance);
                }

                element.focus();
            }
        }, 0);
    }

    public static forceControlsDirty(fg: FormGroup): void {
        Object.keys(fg.controls).forEach((key: string) => {
            if (fg.controls[key] instanceof FormArray) {
                // eslint-disable-next-line @typescript-eslint/dot-notation
                fg.get(key)['controls'].forEach((c: AbstractControl) => {
                    FormUtil.forceControlDirty(c, true);
                });
            }
            FormUtil.forceControlDirty(fg.controls[key], true);
        });
    }

    public static forceControlDirty(control: AbstractControl, forceEvent: boolean = false): void {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity({
            emitEvent: forceEvent
        });
    }

    public static submitForm(fg: FormGroup, callback: Function): void {
        FormUtil.blurActiveControl();
        FormUtil.forceControlsDirty(fg);
        FormUtil.focusFirstError();
        callback();
    }

    public static assignErrors(errors: ApiError[], allInputErrors: XmInputErrorCollection, form: FormGroup): boolean {
        let foundInputError: boolean = false;

        Object.keys(form.controls).forEach((key: string) => {
            const formControl: AbstractControl = form.get(key);
            const inputErrors: XmInputError[] = allInputErrors[key] || [];

            errors.forEach((apiError: ApiError) => {
                const validError: XmInputError = inputErrors.find((inputError: XmInputError) => inputError.code === apiError.code);

                if (validError) {
                    foundInputError = true;
                    formControl.markAsTouched();
                    formControl.markAsDirty();
                    formControl.setErrors({
                        code: apiError.code
                    });
                }
            });
        });

        return foundInputError;
    }

    public static validators(errors: XmInputError[] = []): ValidatorFn[] {
        const validators: ValidatorFn[] = [];
        errors.forEach((error: XmInputError) => {
            if (error.code === 'required') {
                validators.push(this.requiredValidator(error));
            } else if (error.regex) {
                validators.push(this.regexValidator(error));
            } else if (error.minLength) {
                validators.push(this.lengthValidator(error));
            } else if (error.maxLength) {
                validators.push(this.maxLengthValidator(error));
            } else if (error.code === 'minimum-payment') {
                validators.push(this.minimumAmountValidator(error));
            }
        });

        return validators;
    }

    private static regexValidator(error: XmInputError): ValidatorFn {
        return (control: AbstractControl): XmInputError => !new RegExp(error.regex).test(control.value) ? error : undefined;
    }

    private static lengthValidator(error: XmInputError): ValidatorFn {
        return (control: AbstractControl): XmInputError => (control.value || '').length < error.minLength ? error : undefined;
    }

    private static maxLengthValidator(error: XmInputError): ValidatorFn {
        return (control: AbstractControl): XmInputError => (control.value || '').length > error.maxLength ? error : undefined;
    }

    private static requiredValidator(error: XmInputError): ValidatorFn {
        return (control: AbstractControl): XmInputError => !control.value ? error : undefined;
    }

    private static minimumAmountValidator(error: XmInputError): ValidatorFn {
        return (control: AbstractControl): XmInputError =>  (Number(control.value.slice(1)) < Number(MinimumPayment.AMOUNT) ? error : undefined);
    }
}
